import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import {
  webhookService,
  type DeliveryStatus,
  type WebhookDelivery,
  type WebhookEndpoint,
} from '@services/webhookService';
import { formatDateTime } from '@utils/format';
import { deliveryErrorText } from '@utils/webhookDeliveryError';

interface DeliveryLogDialogProps {
  open: boolean;
  orgId: string | null;
  endpoint: WebhookEndpoint | null;
  onClose: () => void;
}

const STATUS_COLOR: Record<DeliveryStatus, 'success' | 'warning' | 'error' | 'default'> = {
  delivered: 'success',
  retrying: 'warning',
  failed: 'error',
  pending: 'default',
};

/**
 * The delivery log for one endpoint (REV-442).
 *
 * This is a debugging surface: when a customer says "my webhook isn't
 * arriving", this is what answers them. So it shows the things that actually
 * distinguish the causes — the HTTP status their server returned, the transport
 * error if there wasn't one, how many attempts have been made, and when the
 * next is due.
 *
 * The API deliberately does not return the event payload here, so neither does
 * this: a log row per event body would be a bulk export of everything the
 * organization has emitted, wearing a debugging label.
 */
export default function DeliveryLogDialog({
  open,
  orgId,
  endpoint,
  onClose,
}: DeliveryLogDialogProps) {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!open || !orgId || !endpoint) return;
    setLoading(true);
    setError(null);
    try {
      const response = await webhookService.listDeliveries(orgId, endpoint.id, { limit: 50 });
      setDeliveries(response.data);
    } catch {
      setError(t('webhooks.deliveries.loadError'));
    } finally {
      setLoading(false);
    }
  }, [open, orgId, endpoint, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const describeResponse = (delivery: WebhookDelivery): string => {
    if (delivery.lastResponseCode !== null) {
      return String(delivery.lastResponseCode);
    }
    // A code, worded here (REV-1026); the API never sends text about the target.
    const failure = deliveryErrorText(t, delivery);
    if (failure) {
      return failure;
    }
    return t('webhooks.deliveries.noResponse');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="delivery-log-title"
    >
      <DialogTitle id="delivery-log-title">
        {t('webhooks.deliveries.title', { name: endpoint?.name ?? '' })}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label={t('common.loading')} />
          </Box>
        ) : error ? (
          <Alert severity="error" role="alert">
            {error}
          </Alert>
        ) : deliveries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: '1.1rem', mb: 1 }}>
              {t('webhooks.deliveries.empty')}
            </Typography>
            <Typography color="text.secondary">{t('webhooks.deliveries.emptyHelp')}</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <caption style={visuallyHidden}>
                {t('webhooks.deliveries.title', { name: endpoint?.name ?? '' })}
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell component="th" scope="col">
                    {t('webhooks.deliveries.event')}
                  </TableCell>
                  <TableCell component="th" scope="col">
                    {t('webhooks.deliveries.status')}
                  </TableCell>
                  <TableCell component="th" scope="col">
                    {t('webhooks.deliveries.attempts')}
                  </TableCell>
                  <TableCell component="th" scope="col">
                    {t('webhooks.deliveries.response')}
                  </TableCell>
                  <TableCell component="th" scope="col">
                    {t('webhooks.deliveries.when')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.map((delivery) => (
                  <TableRow key={delivery.id}>
                    <TableCell component="th" scope="row" sx={{ fontFamily: 'monospace' }}>
                      {delivery.eventType}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`webhooks.deliveryStatus.${delivery.status}`)}
                        color={STATUS_COLOR[delivery.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{delivery.attemptCount}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      {describeResponse(delivery)}
                    </TableCell>
                    <TableCell>
                      {formatDateTime(delivery.lastAttemptAt ?? delivery.createdAt, {
                        dateStyle: 'short',
                        timeStyle: 'medium',
                      })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        {/* outlined for the same reason as the other dialogs (CSS-16). */}
        <Button variant="outlined" onClick={onClose}>
          {t('webhooks.deliveries.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
